/**
 * Cliente Socket.io del player con reconexión exponencial.
 * Tolerante a:
 *  - Cortes de red
 *  - Backend reiniciando
 *  - Tokens vencidos (re-pair manual)
 */

import { io, type Socket } from 'socket.io-client';

import { loadAuthToken } from './storage';

type EventHandler = (...args: unknown[]) => void;

const WS_BASE = (import.meta.env.VITE_WS_URL ?? import.meta.env.VITE_API_URL ?? 'ws://localhost:3010').replace(
  /\/$/,
  '',
);

export type PlayerSocket = ReturnType<typeof connect>;

export function connect() {
  const token = loadAuthToken();
  if (!token) throw new Error('Cannot connect WS without auth token');

  const socket: Socket = io(`${WS_BASE}/player`, {
    transports: ['websocket'],
    auth: { token },
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 30_000,
    randomizationFactor: 0.5,
    timeout: 20_000,
  });

  return {
    socket,
    on(event: string, handler: EventHandler) {
      socket.on(event, handler);
      return () => socket.off(event, handler);
    },
    emit(event: string, payload?: unknown) {
      socket.emit(event, payload);
    },
    close() {
      socket.removeAllListeners();
      socket.close();
    },
    get connected() {
      return socket.connected;
    },
  };
}
